import React, { useState } from 'react';
import { useNotification } from '../hooks/useNotification';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { LifeBuoy, Send, MessageSquare, AlertCircle, ArrowLeft, History } from 'lucide-react';
import { Link } from 'react-router-dom';

export const SupportTicketPage: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('low');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject || !description) {
      showError('All secure fields are required for transmission.');
      return;
    }
    showSuccess('Support transmission sent successfully.');
    setSubject('');
    setDescription('');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">Support Nexus</h1>
          <p className="text-gray-500 font-medium tracking-tight">Access high-priority assistance and manage project inquiries.</p>
        </div>
        <Link to="/profile" className="text-sm font-bold text-primary flex items-center gap-2 hover:gap-3 transition-all mb-1">
          <ArrowLeft className="size-4" /> Identity Center
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        <div className="lg:col-span-8">
          <div className="bg-white dark:bg-black rounded-[2.5rem] p-8 md:p-12 border border-border/40 shadow-sm space-y-10">
            <div className="flex items-center gap-4">
              <div className="size-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                <MessageSquare className="size-6 text-primary" />
              </div>
              <h2 className="text-2xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Initialize New Inquiry</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Transmission Subject</label>
                <Input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Summarize your technical inquiry"
                  className="h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus:ring-2 focus:ring-primary/10 transition-all"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Domain Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus:ring-2 focus:ring-primary/10 appearance-none text-sm"
                  >
                    <option value="general">General Protocols</option>
                    <option value="order">Order Logistics</option>
                    <option value="product">Acquisition Details</option>
                    <option value="payment">Financial Processing</option>
                    <option value="shipping">Global Transit</option>
                    <option value="returns">Reverse Logistics</option>
                    <option value="account">Credential Security</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Priority Level</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus:ring-2 focus:ring-primary/10 appearance-none text-sm"
                  >
                    <option value="low">Standard Response</option>
                    <option value="medium">Elevated Priority</option>
                    <option value="high">Urgent Intervention</option>
                    <option value="urgent">Critical Systems Alert</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1">Inquiry Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  placeholder="Provide comprehensive details of your inquiry..."
                  className="w-full bg-gray-50 dark:bg-gray-900 border-none rounded-[2rem] p-6 font-bold focus:ring-2 focus:ring-primary/10 transition-all text-sm resize-none"
                />
              </div>

              <div className="pt-4">
                <Button type="submit" className="h-16 px-12 rounded-2xl font-black bg-primary hover:bg-primary/90 text-white shadow-2xl shadow-primary/20 transition-all hover:-translate-y-1 flex items-center gap-3">
                  <Send className="size-5" /> Dispatch Inquiry
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar Help */}
        <div className="lg:col-span-4 space-y-8 h-fit">
          <div className="p-10 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] border border-border/40 space-y-8">
            <div className="size-14 bg-white dark:bg-black rounded-2xl flex items-center justify-center shadow-sm">
              <LifeBuoy className="size-7 text-primary" />
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-tight">Emergency Assistance</h3>
              <p className="text-xs font-medium text-gray-400 leading-relaxed uppercase tracking-widest">Global response average: <span className="text-primary">12 Minutes</span></p>
              <Separator className="opacity-50" />
              <div className="flex flex-col gap-4">
                <button className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-[#111418] dark:text-white hover:text-primary transition-colors">
                  <History className="size-4" /> View Open Inquiries
                </button>
                <button className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-[#111418] dark:text-white hover:text-primary transition-colors">
                  <AlertCircle className="size-4" /> Knowledge Base Archive
                </button>
              </div>
            </div>
          </div>

          <div className="p-8 bg-[#111418] rounded-[2rem] text-center space-y-4">
            <p className="text-xs font-black text-white/40 uppercase tracking-[0.2em]">Global Coverage</p>
            <p className="text-sm font-bold text-white tracking-tight">Our agents are active across all timezones to ensure seamless support for your acquisitions.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
